/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable camelcase */
import { handle } from 'redux-pack';
import {
	ADD_GRAPHICS,
	APPEND_GRAPHICS,
	EDIT_GRAPHICS,
	UPDATE_GRAPHICS,
	DELETE_GRAPHICS,
	LOAD_GRAPHICS,
	SET_CURRENT_GRAPHICS_ID,
	SHOW_GRAPHIC_MODAL,
	HIDE_GRAPHIC_MODAL,
	REMOVE_GRAPHICS,
	SET_ENABLED_GRAPHICS,
	REMOVE_SHOT_MODAL_GRAPHIC,
	BULK_EDIT_GRAPHICS,
	SYNC_GRAPHICS_REDUCER_STATE,
	PLAYING_PREVIEW_ANIMATION,
	PREPEND_GRAPHICS,
	ADD_OVERLAYS_UPLOAD_DATA,
	DELETE_OVERLAYS_UPLOAD_DATA,
	SET_ANIMATED_GRAPHICS,
	ADD_ENABLED_GRAPHIC,
	REMOVE_ENABLED_GRAPHIC,
	ADD_ANIMATED_GRAPHIC,
	HIDE_GRAPHIC_FOR_SHOWBUILDER,
	SHOW_GRAPHIC_FOR_SHOWBUILDER,
	CLEAR_HIDDEN_GRAPHICS_FOR_SHOWBUILDER,
	OVERLAY_ENTITY_UPDATED,
	OVERLAY_ENTITY_UPDATED_DRAFT,
	CLEAR_DRAFT_OVERLAYS,
	SET_DRAFT_OVERLAYS,
} from '../actions/graphics';
import Socket from '../../connection/socket';
import { CloudConvertUploadData, Entity } from '../../types/interfaces';
import { arrayAdd } from '../../utils/utils';
import { EntityType } from '../../types/enums';
import type {
	AnimationType,
	GraphicsShots,
	OverlayUploadData,
	GraphicInterface,
	ShotOverlay,
	ShotOverlayData,
	GraphicsState,
} from '../types/graphics';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
let socket: Socket | null = null;

interface GraphicsAction {
	type: string;
	payload: any;
	id?: number;
}

export const initialGraphicsState: GraphicsState = {
	graphics: [],
	currentGraphicId: 0,
	graphicsError: '',
	loadingGraphics: false,
	showGraphicModal: false,
	draggingGraphicId: 0,
	shotModalGraphics: [],
	enabledGraphics: null,
	isPreviewAnimationPlaying: false,
	overlaysUploadData: [],
	animatedGraphics: [],
	enabledOverlays: [],
	animatedOverlays: [],
	allShotOverlaysEntity: null,
	animatedOverlayIdsEntity: null,
	disabledShowBuilderOverlays: [],
};

const getPosData = (keep: GraphicInterface, merge: GraphicInterface) => {
	const newScaleY = merge.scale_y;
	const oldScaleY = keep.scale_y;
	const newScaleX = merge.scale_x;

	// difference in Y scale between old and new to calc expected X scale
	const scalePercY = oldScaleY / newScaleY;
	const newModalScaleX = newScaleX * scalePercY;
	return {
		pos_x: keep.pos_x,
		pos_y: keep.pos_y,
		scale_x: newModalScaleX,
		scale_y: oldScaleY,
		rotate: keep.rotate,
	};
};

const mergePositions = (
	modalGraphics: GraphicInterface[],
	newGraphics: GraphicInterface[],
) => {
	const shotModalGraphics: GraphicInterface[] = newGraphics
		.reduce((acc: GraphicInterface[], newOverlay: GraphicInterface) => {
			const modalOverlay = modalGraphics.find((g: GraphicInterface) => g.id === newOverlay.id);

			if (!modalOverlay) {
				return [...acc, newOverlay];
			}

			const posData = getPosData(modalOverlay, newOverlay);

			const returnedOverlay = {
				...newOverlay,
				...posData,
			};

			return [...acc, returnedOverlay];
		}, [] as GraphicInterface[]);
	return shotModalGraphics;
};

const reduceFunction = (newArray: GraphicInterface[], existingOverlay: GraphicInterface) => {
	if (newArray[0].id === existingOverlay.id) {
		// we found the existing overlay we set as the first item in the new array
		const newOverlay = newArray.shift();
		if (newOverlay) {
			newArray.push(newOverlay);
		}
	} else {
		newArray.push(existingOverlay);
	}
	return newArray;
};

const actions: Map<any, Function> = new Map([
	[
		ADD_GRAPHICS,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => handle(state, action, {
			start: (state) => ({ ...state }),
			finish: (state) => ({ ...state }),
			failure: (state) => ({
				...state,
				graphicsError: 'Error loading graphics. Please try again later.',
			}),
			success: (state) => ({ ...state }),
		}),
	],
	[
		PREPEND_GRAPHICS,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => ({
			...state,
			graphics: [action.payload, ...state.graphics],
			overlaysUploadData: state.overlaysUploadData.filter(
				(d: OverlayUploadData) => d.uploadId !== action.payload.upload_id,
			),
		}),
	],
	[APPEND_GRAPHICS, (state: GraphicsState, action: GraphicsAction): GraphicsState => ({
		...state,
		graphics: [
			...state.graphics,
			action.payload,
		],
	})],
	[
		EDIT_GRAPHICS,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => handle(state, action, {
			start: (state) => ({ ...state }),
			finish: (state) => ({ ...state }),
			failure: (state) => ({
				...state,
				graphicsError: 'Error loading graphics. Please try again later.',
			}),
			success: (state) => ({ ...state }),
		}),
	],
	[
		UPDATE_GRAPHICS,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => ({
			...state,
			graphics: state.graphics.map((graphic: GraphicInterface) => (
				graphic.id === action.payload.id ? action.payload : graphic
			)),
			shotModalGraphics: state.shotModalGraphics.map((graphic: GraphicInterface) => (
				graphic.id === action.payload.id
					? { ...action.payload, ...getPosData(graphic, action.payload) }
					: graphic
			)),
		}),
	],
	[
		DELETE_GRAPHICS,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => handle(state, action, {
			start: (state) => ({ ...state }),
			finish: (state) => ({ ...state }),
			failure: (state) => ({
				...state,
				graphicsError: 'Error loading graphics. Please try again later.',
			}),
			success: (state) => ({ ...state }),
		}),
	],
	[
		LOAD_GRAPHICS,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => handle(state, action, {
			start: (state) => ({ ...state, loadingGraphics: true }),
			finish: (state) => ({ ...state, loadingGraphics: false }),
			failure: (state) => ({
				...state,
				graphicsError: 'Error loading graphics. Please try again later.',
			}),
			success: (state) => {
				const currentEnabledGraphics: number[] | null = state.enabledGraphics
					? [...state.enabledGraphics]
					: null;
				const graphics: GraphicInterface[] = Array.isArray(action.payload)
					? action.payload
					: [];
				// Sync runs before this, only set defaults if
				const enabledGraphics = currentEnabledGraphics || graphics.reduce<number[]>(
					(idArr: number[], graphic: GraphicInterface) => {
						const { id } = graphic;

						if (id && graphic?.animation?.build_in !== 1) {
							return [...idArr, id];
						}

						return idArr;
					},
					[],
				);

				const graphicsInModal = graphics
					.reduce((acc, g) => {
						const isInModal = state.shotModalGraphics.some((modalG) => modalG.id === g.id);
						return isInModal ? [...acc, g] : acc;
					}, [] as GraphicInterface[]);

				const shotModalGraphics = mergePositions(state.shotModalGraphics, graphicsInModal);

				return {
					...state,
					graphics,
					enabledGraphics,
					shotModalGraphics,
				};
			},
		}),
	],
	[SET_CURRENT_GRAPHICS_ID, (state: GraphicsState, action: GraphicsAction): GraphicsState => ({
		...state,
		currentGraphicId: action.payload,
	})],
	[SHOW_GRAPHIC_MODAL, (state: GraphicsState): GraphicsState => ({
		...state,
		showGraphicModal: true,
	})],
	[HIDE_GRAPHIC_MODAL, (state: GraphicsState): GraphicsState => ({
		...state,
		showGraphicModal: false,
	})],
	[REMOVE_GRAPHICS, (state: GraphicsState, action: GraphicsAction): GraphicsState => ({
		...state,
		graphics: state.graphics.filter((graphic: GraphicInterface) => graphic.id !== action.payload),
	})],
	[SET_ENABLED_GRAPHICS, (state: GraphicsState, action: GraphicsAction): GraphicsState => ({
		...state,
		enabledGraphics: action.payload,
	})],
	[ADD_ENABLED_GRAPHIC, (state: GraphicsState, action: GraphicsAction): GraphicsState => {
		const newGraphicId: number = action.payload;
		const updateEnabledGraphics: number[] = state.enabledGraphics
			? arrayAdd(state.enabledGraphics, newGraphicId)
			: [newGraphicId];

		return {
			...state,
			enabledGraphics: updateEnabledGraphics,
		};
	}],
	[REMOVE_ENABLED_GRAPHIC, (state: GraphicsState, action: GraphicsAction): GraphicsState => {
		const removeGraphicId: number = action.payload;
		const updateEnabledGraphics: number[] = state.enabledGraphics
			? state.enabledGraphics.filter((graphicId) => graphicId !== removeGraphicId)
			: [];

		return {
			...state,
			enabledGraphics: updateEnabledGraphics,
		};
	}],
	[REMOVE_SHOT_MODAL_GRAPHIC, (state: GraphicsState, action: GraphicsAction): GraphicsState => {
		const shotModalGraphics = state.shotModalGraphics.filter(
			(shotModalGraphic: GraphicInterface) => shotModalGraphic.id !== action.payload,
		);
		return {
			...state, shotModalGraphics,
		};
	}],
	[PLAYING_PREVIEW_ANIMATION, (state: GraphicsState, action: GraphicsAction) => ({
		...state,
		isPreviewAnimationPlaying: action.payload,
	})],
	[BULK_EDIT_GRAPHICS, (state: GraphicsState, action: GraphicsAction) => handle(state, action, {
		start: (state) => ({ ...state }),
		finish: (state) => ({ ...state }),
		failure: (state) => ({ ...state, graphicsError: 'Error editing graphics. Please try again later.' }),
		success: (state) => ({ ...state }),
	})],
	[SYNC_GRAPHICS_REDUCER_STATE, (state: GraphicsState, action: GraphicsAction): GraphicsState => ({
		...state,
		...action.payload,
	})],
	[
		ADD_OVERLAYS_UPLOAD_DATA,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => {
			const newUploading = state.overlaysUploadData.filter(
				(d: OverlayUploadData) => d.uploadId !== action.payload.uploadId,
			);
			return {
				...state,
				overlaysUploadData: [...newUploading, action.payload],
			};
		},
	],
	[
		DELETE_OVERLAYS_UPLOAD_DATA,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => ({
			...state,
			overlaysUploadData: state.overlaysUploadData.filter(
				(d: OverlayUploadData) => d.uploadId !== action.payload.uploadId,
			),
		}),
	],
	[SET_ANIMATED_GRAPHICS, (state: GraphicsState, action: GraphicsAction): GraphicsState => ({
		...state,
		animatedGraphics: action.payload,
	})],
	[ADD_ANIMATED_GRAPHIC, (state: GraphicsState, action: GraphicsAction): GraphicsState => {
		const newAnimatedGraphicId: number = action.payload;
		const updateAnimatedGraphics: number[] = state.animatedGraphics
			? arrayAdd(state.animatedGraphics, newAnimatedGraphicId)
			: [newAnimatedGraphicId];

		return {
			...state,
			animatedGraphics: updateAnimatedGraphics,
		};
	}],
	[
		EntityType.allShotOverlays,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => {
			const allShotOverlays: ShotOverlayData | null = action.payload
				?.entityValue?.allShotOverlays ?? null;
			const updateEnabledOverlays: number[] | null = allShotOverlays?.overlayIds.reduce<
				number[]
			>((overlayIdArr: number[], overlayId: number) => {
				if (allShotOverlays.overlays[overlayId].isEnabled) {
					return [...overlayIdArr, overlayId];
				}

				return overlayIdArr;
			}, []) ?? null;

			return {
				...state,
				// TODO: just for debugging, remove this before merging
				allShotOverlaysEntity: action.payload,
				...(updateEnabledOverlays && { enabledOverlays: updateEnabledOverlays }),
			};
		},
	],
	[
		EntityType.animatedOverlayIds,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => {
			const animatedOverlayIds: number[] | null = action.payload
				?.entityValue?.animatedOverlayIds ?? null;

			return {
				...state,
				// TODO: just for debugging, remove this before merging
				animatedOverlayIdsEntity: action.payload,
				...(animatedOverlayIds && { animatedOverlays: animatedOverlayIds }),
			};
		},
	],
	[
		HIDE_GRAPHIC_FOR_SHOWBUILDER,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => {
			const { disabledShowBuilderOverlays = [] } = state;
			const newDisabledShowBuilderOverlays = [...disabledShowBuilderOverlays];
			const { payload } = action;
			const index = newDisabledShowBuilderOverlays.indexOf(payload);

			if (index === -1) {
				newDisabledShowBuilderOverlays.push(payload);
			}

			return {
				...state,
				disabledShowBuilderOverlays: newDisabledShowBuilderOverlays,
			};
		},
	],
	[
		SHOW_GRAPHIC_FOR_SHOWBUILDER,
		(state: GraphicsState, action: GraphicsAction): GraphicsState => {
			const { disabledShowBuilderOverlays = [] } = state;
			const newDisabledShowBuilderOverlays = [...disabledShowBuilderOverlays];
			const { payload } = action;

			const index = newDisabledShowBuilderOverlays.indexOf(payload);
			if (index > -1) {
				newDisabledShowBuilderOverlays.splice(index, 1);
			}

			return {
				...state,
				disabledShowBuilderOverlays: newDisabledShowBuilderOverlays,
			};
		},
	],
	[
		CLEAR_HIDDEN_GRAPHICS_FOR_SHOWBUILDER,
		(state: GraphicsState): GraphicsState => ({
			...state,
			disabledShowBuilderOverlays: [],
		}),
	],
	[
		OVERLAY_ENTITY_UPDATED,
		(state: GraphicsState, action: GraphicsAction) => ({
			...state,
			graphics: state.graphics.reduce(
				reduceFunction,
				[action.payload.entityValue],
			),
		}),
	],
	[
		OVERLAY_ENTITY_UPDATED_DRAFT,
		(state: GraphicsState, action: GraphicsAction) => ({
			...state,
			shotModalGraphics: state.shotModalGraphics.reduce(
				reduceFunction,
				[action.payload.entityValue],
			),
		}),
	],
	[
		SET_DRAFT_OVERLAYS,
		(state: GraphicsState, action: GraphicsAction) => ({
			...state,
			shotModalGraphics: action.payload,
		}),
	],
	[
		CLEAR_DRAFT_OVERLAYS,
		(state: GraphicsState) => ({
			...state,
			shotModalGraphics: [],
		}),
	],
]);

export const GraphicsReducer = (
	// eslint-disable-next-line default-param-last
	state: GraphicsState = initialGraphicsState,
	action: GraphicsAction,
): GraphicsState => actions.get(action.type)?.(state, action) ?? state;

export function setGraphicsReducerSocket(sock: Socket): void {
	socket = sock;
}
